import React, { useState, useMemo, useEffect } from 'react';
import { Search, ChevronLeft, ChevronRight, Check, X, Layers, Trash2, Code, Monitor, Server, Cloud, LucideIcon } from 'lucide-react';
import { SVG_CATALOG, SvgIconItem, SvgBrand, getCatalogForBrand } from './svgCatalog';
import { BrandIcon } from './BrandIcon';

export interface SvgCategoryItem {
  id: SvgBrand;
  slug?: string;
  label: string;
  icon: LucideIcon | React.ComponentType<{ size?: number | string; className?: string }>;
}

export const CATEGORIES: SvgCategoryItem[] = [
  { id: 'aws', slug: 'aws', label: 'AWS', icon: Cloud },
  { id: 'azure', slug: 'azure', label: 'Azure', icon: Cloud },
  { id: 'gcp', slug: 'gcp', label: 'Google Cloud', icon: Cloud },
  { id: 'devtool', slug: 'DevTools', label: 'Dev Tools', icon: Code },
  { id: 'microsoft', slug: 'Microsoft', label: 'Microsoft', icon: Monitor },
  { id: 'hosting', slug: 'Hosting', label: 'Hosting', icon: Server },
];

interface SvgIconModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (selectedIcons: SvgIconItem[]) => void;
  styles: any;
  isDark: boolean;
  initialBrand?: SvgBrand | string;
}

const ITEMS_PER_PAGE = 20; // 5 columns x 4 rows = 20 items per page (4 rows per page)

export const SvgIconModal: React.FC<SvgIconModalProps> = ({
  isOpen,
  onClose,
  onInsert,
  styles,
  isDark,
  initialBrand = 'aws'
}) => {
  const [activeBrand, setActiveBrand] = useState<SvgBrand>(() => {
    if (initialBrand) {
      if (initialBrand in SVG_CATALOG) return initialBrand as SvgBrand;
      const lower = initialBrand.toLowerCase();
      if (lower === 'devtools') return 'devtool';
      if (lower in SVG_CATALOG) return lower as SvgBrand;
    }
    return 'aws';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMap, setSelectedMap] = useState<Record<string, SvgIconItem>>({});

  useEffect(() => {
    if (initialBrand) {
      if (initialBrand in SVG_CATALOG) {
        setActiveBrand(initialBrand as SvgBrand);
      } else {
        const lower = initialBrand.toLowerCase();
        if (lower === 'devtools') setActiveBrand('devtool');
        else if (lower in SVG_CATALOG) setActiveBrand(lower as SvgBrand);
      }
    }
  }, [initialBrand, isOpen]);

  // Reset page when brand or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeBrand, searchQuery]);

  const currentBrandCatalog = useMemo(() => {
    return getCatalogForBrand(activeBrand);
  }, [activeBrand]);

  const filteredIcons = useMemo(() => {
    if (!searchQuery.trim()) return currentBrandCatalog;
    const q = searchQuery.toLowerCase();
    return currentBrandCatalog.filter(
      item => item.name.toLowerCase().includes(q) || item.key.toLowerCase().includes(q)
    );
  }, [currentBrandCatalog, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredIcons.length / ITEMS_PER_PAGE));

  const pageIcons = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredIcons.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredIcons, currentPage]);

  const selectedList = useMemo(() => Object.values(selectedMap), [selectedMap]);
  const selectedCount = selectedList.length;

  if (!isOpen) return null;

  const toggleSelect = (item: SvgIconItem) => {
    setSelectedMap(prev => {
      const next = { ...prev };
      if (next[item.key]) {
        delete next[item.key];
      } else {
        next[item.key] = item;
      }
      return next;
    });
  };

  const isPageAllSelected = pageIcons.length > 0 && pageIcons.every(item => Boolean(selectedMap[item.key]));

  const toggleSelectPage = () => {
    setSelectedMap(prev => {
      const next = { ...prev };
      if (isPageAllSelected) {
        pageIcons.forEach(item => {
          delete next[item.key];
        });
      } else {
        pageIcons.forEach(item => {
          next[item.key] = item;
        });
      }
      return next;
    });
  };

  const handleClearSelection = () => {
    setSelectedMap({});
  };

  const handleInsert = () => {
    if (selectedCount === 0) return;
    onInsert(selectedList);
    setSelectedMap({});
    onClose();
  };

  const brandTabColors: Record<string, { active: string; badge: string }> = {
    aws: {
      active: isDark ? 'bg-amber-500/20 text-amber-400 border-amber-500' : 'bg-amber-50 text-amber-600 border-amber-500',
      badge: 'bg-amber-500 text-slate-950',
    },
    azure: {
      active: isDark ? 'bg-blue-500/20 text-blue-400 border-blue-500' : 'bg-blue-50 text-blue-600 border-blue-500',
      badge: 'bg-blue-500 text-white',
    },
    gcp: {
      active: isDark ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500' : 'bg-emerald-50 text-emerald-600 border-emerald-500',
      badge: 'bg-emerald-500 text-white',
    },
    devtool: {
      active: isDark ? 'bg-purple-500/20 text-purple-400 border-purple-500' : 'bg-purple-50 text-purple-600 border-purple-500',
      badge: 'bg-purple-500 text-white',
    },
    DevTools: {
      active: isDark ? 'bg-purple-500/20 text-purple-400 border-purple-500' : 'bg-purple-50 text-purple-600 border-purple-500',
      badge: 'bg-purple-500 text-white',
    },
    microsoft: {
      active: isDark ? 'bg-sky-500/20 text-sky-400 border-sky-500' : 'bg-sky-50 text-sky-600 border-sky-500',
      badge: 'bg-sky-500 text-white',
    },
    Microsoft: {
      active: isDark ? 'bg-sky-500/20 text-sky-400 border-sky-500' : 'bg-sky-50 text-sky-600 border-sky-500',
      badge: 'bg-sky-500 text-white',
    },
    hosting: {
      active: isDark ? 'bg-orange-500/20 text-orange-400 border-orange-500' : 'bg-orange-50 text-orange-600 border-orange-500',
      badge: 'bg-orange-500 text-white',
    },
    Hosting: {
      active: isDark ? 'bg-orange-500/20 text-orange-400 border-orange-500' : 'bg-orange-50 text-orange-600 border-orange-500',
      badge: 'bg-orange-500 text-white',
    },
  };

  const currentCategoryLabel = CATEGORIES.find(c => c.id === activeBrand || (c.slug && c.slug.toLowerCase() === (activeBrand as string).toLowerCase()))?.label || activeBrand;

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center ${styles.modalOverlay} backdrop-blur-md p-4 animate-in fade-in`}
      onClick={onClose}
    >
      <div
        className={`${styles.modalBg} w-full max-w-4xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl border ${styles.panelBorder} overflow-hidden relative`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`p-5 border-b ${styles.panelBorder} flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-blue-500/10 text-blue-400 border border-blue-500/20">
              <Layers size={22} />
            </div>
            <div>
              <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Cloud Icon Library
              </h2>
              <p className={`text-xs ${styles.textSubtle}`}>
                Browse, multi-select, and insert cloud, dev tool, and hosting icons onto canvas
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg hover:bg-slate-500/10 ${styles.textSubtle} transition-colors`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Tab & Search Bar */}
        <div className={`p-4 border-b ${styles.panelBorder} flex flex-col lg:flex-row items-center justify-between gap-3 bg-slate-500/5`}>
          {/* Brand / Category Tabs */}
          <div className="flex items-center gap-1.5 flex-wrap w-full lg:w-auto">
            {CATEGORIES.map(cat => {
              const isActive = activeBrand === cat.id || (activeBrand as string).toLowerCase() === cat.id.toLowerCase();
              const count = getCatalogForBrand(cat.id).length;
              const IconComponent = cat.icon;
              const colors = brandTabColors[cat.id] || brandTabColors.devtool;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveBrand(cat.id)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all flex items-center justify-center gap-1.5 ${
                    isActive
                      ? colors.active
                      : `${isDark ? 'hover:bg-slate-800 text-slate-400 border-transparent' : 'hover:bg-slate-100 text-slate-600 border-transparent'}`
                  }`}
                >
                  <IconComponent size={14} className="opacity-75" />
                  <span>{cat.label}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${isActive ? colors.badge : 'bg-slate-500/20 text-slate-400'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Search Field */}
          <div className="relative w-full lg:w-64">
            <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${styles.textSubtle}`} />
            <input
              type="text"
              placeholder={`Search ${currentCategoryLabel} icons...`}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className={`w-full ${styles.inputBg} border ${styles.inputBorder} rounded-xl pl-9 pr-8 py-2 text-xs outline-none focus:ring-2 focus:ring-blue-500/40`}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className={`absolute right-2.5 top-1/2 -translate-y-1/2 ${styles.textSubtle} hover:text-white`}
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Selection Bar & Pagination Header */}
        <div className={`px-5 py-2.5 border-b ${styles.panelBorder} flex flex-wrap items-center justify-between text-xs gap-2 bg-slate-500/5`}>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleSelectPage}
              className={`px-2.5 py-1 rounded-lg border text-[11px] font-medium transition-colors ${
                isPageAllSelected
                  ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                  : `${styles.inputBg} ${styles.inputBorder} ${styles.textSubtle} hover:text-white`
              }`}
            >
              {isPageAllSelected ? 'Deselect Page' : 'Select Page'}
            </button>
            {selectedCount > 0 && (
              <button
                onClick={handleClearSelection}
                className="px-2.5 py-1 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 text-[11px] font-medium transition-colors flex items-center gap-1"
              >
                <Trash2 size={12} />
                <span>Clear ({selectedCount})</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <span className={styles.textSubtle}>
              Showing {filteredIcons.length > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0}-
              {Math.min(currentPage * ITEMS_PER_PAGE, filteredIcons.length)} of {filteredIcons.length}
            </span>

            {/* Pagination Controls */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={`p-1.5 rounded-lg border ${styles.inputBorder} ${styles.inputBg} disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-500/20 transition-colors`}
                title="Previous Page"
              >
                <ChevronLeft size={16} />
              </button>
              <span className={`px-2 font-mono text-xs ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className={`p-1.5 rounded-lg border ${styles.inputBorder} ${styles.inputBg} disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-500/20 transition-colors`}
                title="Next Page"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Icon Grid (5 columns x 4 rows = 20 items per page) */}
        <div className="p-5 overflow-y-auto flex-1 min-h-[320px]">
          {pageIcons.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Search size={36} className={`${styles.textSubtle} mb-2 opacity-40`} />
              <p className={`font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                No icons found
              </p>
              <p className={`text-xs ${styles.textSubtle} mt-1`}>
                Try adjusting your search query or switching brand tabs.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {pageIcons.map(item => {
                const isSelected = Boolean(selectedMap[item.key]);
                return (
                  <div
                    key={item.key}
                    onClick={() => toggleSelect(item)}
                    className={`relative group rounded-xl p-3 border-2 transition-all cursor-pointer flex flex-col items-center justify-between text-center select-none ${
                      isSelected
                        ? 'border-blue-500 bg-blue-500/10 shadow-lg shadow-blue-500/10 ring-1 ring-blue-500/50'
                        : `${styles.inputBorder} ${styles.inputBg} hover:border-slate-400/50 hover:bg-slate-500/10`
                    }`}
                  >
                    {/* Checkmark Badge */}
                    <div
                      className={`absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                        isSelected
                          ? 'bg-blue-500 text-white scale-100'
                          : 'border border-slate-500/40 text-transparent scale-90 opacity-0 group-hover:opacity-100'
                      }`}
                    >
                      <Check size={12} strokeWidth={3} />
                    </div>

                    {/* Icon Render Container */}
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center my-2 p-1 border transition-colors ${
                      isDark 
                        ? 'bg-slate-800/90 border-slate-700/60 text-slate-100 shadow-inner' 
                        : 'bg-slate-100/90 border-slate-200/80 text-slate-900 shadow-sm'
                    }`}>
                      <div className="w-8 h-8 flex items-center justify-center overflow-hidden">
                        <BrandIcon
                          type={item.key}
                          brand={item.brand}
                          iconKey={item.key}
                          size={28}
                          isDark={isDark}
                        />
                      </div>
                    </div>

                    {/* Icon Label */}
                    <span className={`text-[11px] font-medium leading-tight line-clamp-2 mt-1 ${
                      isSelected ? 'text-blue-400 font-semibold' : isDark ? 'text-slate-300' : 'text-slate-700'
                    }`}>
                      {item.name}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer Bar */}
        <div className={`p-4 border-t ${styles.panelBorder} flex items-center justify-between bg-slate-500/5`}>
          <div className="text-xs">
            {selectedCount > 0 ? (
              <span className="font-semibold text-blue-400 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                {selectedCount} icon{selectedCount > 1 ? 's' : ''} selected
              </span>
            ) : (
              <span className={styles.textSubtle}>Select one or more icons to insert</span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded-xl border ${styles.inputBorder} ${styles.textSubtle} hover:text-white text-xs font-medium transition-colors`}
            >
              Cancel
            </button>
            <button
              onClick={handleInsert}
              disabled={selectedCount === 0}
              className={`px-5 py-2 rounded-xl font-semibold text-xs transition-all flex items-center gap-2 ${
                selectedCount > 0
                  ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/25 active:scale-95'
                  : 'bg-slate-700/50 text-slate-500 cursor-not-allowed border border-slate-700'
              }`}
            >
              <span>Insert Selected ({selectedCount})</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
